import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import SEOHead from './components/SEOHead'; // Import our new SEOHead component
import PrivateRoute from './routes/PrivateRoute';
import AdminRoute from './routes/AdminRoute';
import Register from './components/auth/Register';
import UserProfile from './components/user/UserProfile';
import YogaClassCard from './components/YogaClassCard';
import Login from './components/auth/Login';
import Schedule from './components/schedule/Schedule';
import ClassesSection from './components/classes/ClassesSection';
import AdminDashboard from './components/admin/AdminDashboard';
import Header from './components/home/Header';
import WelcomeSection from './components/home/WelcomeSection';
import InstructorSlider from './components/instructors/InstructorSlider';
import InstructorProfile from './components/instructors/InstructorProfile';
import MyBookings from './components/user/MyBookings';
import AboutUs from './components/about/AboutUs';
import YogaLore from './components/yogaLore/YogaLore';
import ParallaxSection from './components/home/ParallaxSection';
import Sponsors from './components/home/Sponsors';
import Footer from './components/home/Footer';
import Workshops from './components/workshops/Workshops';
import WorkshopDetails from './components/workshops/WorkshopDetails';
import ClaimAccount from './pages/ClaimAccount';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import PasswordGate from './components/PasswordGate';
import { checkTokenValidation } from './axiosSetup';
import Partners from './components/home/Partners';
import KidsSpace from './components/home/KidsSpace';
import KidsSpacePage from './components/kids/KidsSpacePage';
import ClassPage from './components/schedule/ClassPage';
import { KIDS_SPACE_PUBLIC, KIDS_SPACE_PREVIEW_EMAILS } from './config/kidsSpaceConfig';
import './App.css';

// Teaser section shown on the main page
const KidsSpaceConditional = () => {
  const { currentUser } = useAuth();
  if (KIDS_SPACE_PUBLIC) return <KidsSpace />;
  const canPreview = currentUser && KIDS_SPACE_PREVIEW_EMAILS.includes(currentUser.email);
  return canPreview ? <KidsSpace /> : null;
};

// Full detail page at /kids-space
const KidsSpacePageConditional = () => {
  const { currentUser } = useAuth();
  if (KIDS_SPACE_PUBLIC) return <KidsSpacePage />;
  const canPreview = currentUser && KIDS_SPACE_PREVIEW_EMAILS.includes(currentUser.email);
  return canPreview ? <KidsSpacePage /> : <Navigate to="/" replace />;
};

function App() {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [language, setLanguage] = useState('en'); // Default language
  const [isPasswordVerified, setIsPasswordVerified] = useState(true);

  // Handler for password verification
  const handlePasswordSuccess = () => {
    setIsPasswordVerified(true);
  };

  // Detect user language preference
  useEffect(() => {
    const userLang = navigator.language || navigator.userLanguage;
    if (userLang.startsWith('sk')) {
      setLanguage('sk');
    }
  }, []);

  // Check for language in localStorage if available
  useEffect(() => {
    const storedLang = localStorage.getItem('preferredLanguage');
    if (storedLang) {
      setLanguage(storedLang);
    }
  }, []);

  // Run token validation check when the app loads (after password verification)
  useEffect(() => {
    if (!isPasswordVerified) return;
    
    // Run token validation debug check
    const runTokenValidation = async () => {
      try {
        // First try to get a token
        const response = await axios.post('/bookings/api-token', {}, {
          headers: { 
            'Skip-Auth-Interceptor': 'true'
            // Don't manually set Origin - browser sets it automatically
          }
        });
        
        if (response.data && response.data.token) {
          // console.log('Debug token acquired for validation check');
          
          // Wait a bit before checking validation
          setTimeout(async () => {
            const validationResult = await checkTokenValidation();
            // console.log('Initial token validation check:', validationResult);
          }, 1000);
        }
      } catch (err) {
        console.error('Error in token validation check:', err);
      }
    };
    
    // Uncomment for debugging
    // runTokenValidation();
  }, [isPasswordVerified]);

  // Only fetch data after password verification
  useEffect(() => {
    if (!isPasswordVerified) return;
    
    const fetchClasses = async () => {
      try {
        const response = await axios.get('classes');
        setClasses(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching classes:', err);
        setError('Failed to load yoga classes. Please try again later.');
        setLoading(false);
      }
    };

    fetchClasses();
  }, [isPasswordVerified]);

  return (
    <AuthProvider>
      <ThemeProvider>
        <div className="app" lang={language}>
          {/* Use our new SEOHead component instead of inline Helmet */}
          <SEOHead />
          
          {/* Password Protection Gate */}
          {!isPasswordVerified && <PasswordGate onPasswordSuccess={handlePasswordSuccess} />}
          
          {/* Only render site content after password verification */}
          {isPasswordVerified && (
            <>
              <Header />
              
              <main className="main-content">
                <Routes>
                  <Route path="/" element={
                    <>
                      <WelcomeSection />
                      {/* Featured Classes section is commented out
                      <div className="container">
                        {loading ? (
                          <div className="loading">
                            <div className="spinner"></div>
                            <p>Loading yoga classes...</p>
                          </div>
                        ) : error ? (
                          <div className="error-message">{error}</div>
                        ) : (
                          <>
                            <h2 className="section-title">Featured Classes</h2>
                            <div className="classes-grid">
                              {classes.slice(0, 4).map(yogaClass => (
                                <YogaClassCard key={yogaClass.id} yogaClass={yogaClass} />
                              ))}
                            </div>
                            {classes.length === 0 && <p>No yoga classes available.</p>}
                          </>
                        )}
                      </div>
                      */}
                      
                      {/* Schedule Section */}
                      <div className="schedule-section">
                        <div className="container">
                          {/* <h2 className="section-title">Class Schedule</h2> */}
                          <Schedule />
                        </div>
                      </div>
                      
                      {/* Instructors Section */}
                      <InstructorSlider />
                      
                      {/* Kids Space Teaser Section */}
                      <KidsSpaceConditional />
                      
                      {/* Workshops Section */}
                      <Workshops />
                      
                      {/* About Us Section */}
                      <AboutUs />
                      
                      {/* Parallax Quote Section */}
                      <ParallaxSection />
                      
                      {/* Partners Section */}
                      <Partners />
                      
                      {/* Sponsors Section */}
                      <Sponsors />
                    </>
                  } />
                  
                  <Route path="/classes" element={
                    <div className="container">
                      <ClassesSection />
                    </div>
                  } />
                  
                  <Route path="/login" element={<div className="container"><Login /></div>} />
                  <Route path="/register" element={<div className="container"><Register /></div>} />
                  <Route path="/claim-account" element={<div className="container"><ClaimAccount /></div>} />
                  <Route path="/forgot-password" element={<div className="container"><ForgotPassword /></div>} />
                  <Route path="/reset-password" element={<div className="container"><ResetPassword /></div>} />
                  
                  {/* Protected routes using correct outlet pattern */}
                  <Route element={<PrivateRoute />}>
                    <Route path="/profile" element={
                      <div className="container">
                        <UserProfile />
                      </div>
                    } />
                    <Route path="/bookings" element={
                      <div className="container">
                        <MyBookings />
                      </div>
                    } />
                  </Route>
                  
                  {/* Admin routes using correct outlet pattern */}
                  <Route element={<AdminRoute />}>
                    <Route path="/admin/*" element={<AdminDashboard />} />
                  </Route>
                  
                  <Route path="/schedule" element={<div className="container"><Schedule /></div>} />
                  
                  <Route path="/instructors/:id" element={<InstructorProfile />} />
                  
                  <Route path="/about" element={<AboutUs />} />
                  
                  <Route path="/yoga-lore" element={<YogaLore />} />
                  
                  <Route path="/workshops/:id" element={<WorkshopDetails />} />
                  
                  <Route path="/kids-space" element={<KidsSpacePageConditional />} />
                  
                  <Route path="/class/:id" element={<ClassPage />} />
                  
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </main>
              
              <Footer />
            </>
          )}
        </div>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App; 